import { Box, Grid, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import CustomerNameTagButton from '../CustomerNameTagButton';
import {
  ArrayInput,
  BooleanInput,
  FormDataConsumer,
  GetListParams,
  Labeled,
  maxLength,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  TimeInput,
  useDataProvider
} from 'react-admin';
import DateTimeInputUTC from '../DateTimeInputUTC';
import { getChainId } from '../../lib';
import { TagType } from '../../models';
import { RichTextInputCustom } from '../RichTextInputCustom';
import { daysOfTheWeek } from '../../constants';
import NextSendNotice from './NextSendNotice';

const recurrencyFrequencyChoices = [
  { id: 'daily', name: 'Diária' },
  { id: 'weekly', name: 'Semanal' },
];

const daySx = {
  fontSize: 16,
  margin: '20px 0'
};

const inputWrapperSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  margin: '0 0 20px 0',
};

const inputSx = {
  margin: '0 20px 0 0',
};

const FormRecurringSend = () => {
  const titleInputRef = useRef<HTMLInputElement>(null);
  const pushInputRef = useRef<HTMLInputElement>(null);

  const [tagClient, setTagClient] = React.useState<any[]>([]);
  const [tagStation, setTagStation] = React.useState<any[]>([]);
  const [inputUsed, setInputUsed] = React.useState<string>("");

  const [counterCharSms, setCounterCharSms] = React.useState(0);
  const [counterCharPush, setCounterCharPush] = React.useState(0);

  const MAX_LENGTH_SMS = 160;
  const MAX_LENGTH_PUSH = 130;

  const dataProvider = useDataProvider();

  useEffect(() => {
    dataProvider.getList(`chains/${getChainId()}/tag`, { filter: { type: TagType.client }, sort: { field: 'name', order: 'ASC' } } as GetListParams).then(({ data }) => {
      setTagClient(data);
    });
    dataProvider.getList(`chains/${getChainId()}/tag`, { filter: { type: TagType.station }, sort: { field: 'name', order: 'ASC' } } as GetListParams).then(({ data }) => {
      setTagStation(data);
    });
  }, []);

  const changeInputUsed = (inputNameUsed, value) => {
    value ? setInputUsed(inputNameUsed) : setInputUsed("");
  };

  const isDisabled = (value) => {
    return inputUsed !== value && inputUsed !== "";
  };

  const counterCharInput = (inputName: string, valueInput: string) => {
    const valueFormater = valueInput.length;
    switch (inputName) {
      case 'sms':
        setCounterCharSms(valueFormater);
        break;
      case 'push':
        setCounterCharPush(valueFormater);
        break;
    }
  };

  return (
    <>
      {/* Segmentacao */}
      <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>Segmentação</Typography>
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <SelectInput
          label="Etiqueta (clientes)"
          source="customerTagId"
          optionText="name"
          optionValue="id"
          choices={tagClient}
          style={{ width: '25%' }}
          disabled={isDisabled('customerTag')}
          emptyText="ra.action.clear_input_value"
          onChange={(e) => changeInputUsed('customerTag', e.target.value)}
        />
        <p style={{ marginLeft: '1rem', marginRight: '1rem' }}>Ou</p>
        <SelectInput
          label="Etiqueta (postos)"
          source="stationTagId"
          optionText="name"
          optionValue="id"
          choices={tagStation}
          style={{ width: '25%' }}
          disabled={isDisabled('stationTag')}
          emptyText="ra.action.clear_input_value"
          onChange={(e) => changeInputUsed('stationTag', e.target.value)}
        />
        <p style={{ marginLeft: '1rem', marginRight: '1rem' }}>Ou</p>
        <SelectInput
          source="gender"
          label="Sexo do Cliente"
          choices={[
            { id: 'F', name: 'Feminino' },
            { id: 'M', name: 'Masculino' },
          ]}
          style={{ width: '25%' }}
          disabled={isDisabled('gender')}
          emptyText="ra.action.clear_input_value"
          onChange={(e) => changeInputUsed('gender', e.target.value)}
        />
      </div>

      {/* Agendamento */}
      <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>Agendamento</Typography>
      <Grid container spacing={2} sx={{ maxWidth: 500, mb: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimeInputUTC
            source="startAt"
            label="Data inicial"
            validate={[required()]}
            helperText="Só a data é considerada; o horário de envio é definido abaixo."
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimeInputUTC
            source="endAt"
            label="Data final (opcional)"
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid container sx={{ maxWidth: 250 }}>
        <Grid size={12}>
          <SelectInput
            label="Frequência"
            source="frequency"
            alwaysOn
            choices={recurrencyFrequencyChoices}
            validate={[required()]}
            fullWidth
          />
        </Grid>
      </Grid>
      <FormDataConsumer>
        {({ formData }) => {
          if (formData.frequency === 'daily') {
            return (
              <Grid container spacing={2} sx={{ maxWidth: 250 }}>
                <Grid size={12}>
                  <TimeInput
                    source="startTime"
                    label="Horário de envio"
                    isRequired
                    validate={[required()]}
                    fullWidth
                  />
                </Grid>
              </Grid>
            );
          }

          if (formData.frequency === 'weekly') {
            return (
              <ArrayInput source="weekSchedule" label="Defina os dias e horários">
                <SimpleFormIterator disableAdd disableRemove disableReordering>
                  <FormDataConsumer>
                    {({ scopedFormData }) => {
                      if (!scopedFormData) { return <></>; }
                      const day = daysOfTheWeek.find(wd => wd.id === scopedFormData.dayOfTheWeek);
                      return (
                        <>
                          <Typography sx={daySx}>{day?.name}</Typography>
                          <Box sx={inputWrapperSx}>
                            <BooleanInput
                              source="isActive"
                              label={false}
                              sx={inputSx}
                            />
                            {scopedFormData && scopedFormData.isActive && (
                              <TimeInput
                                label='Horário de envio'
                                source="startTime"
                                style={{ marginRight: 20, width: 200 }}
                                isRequired
                              />
                            )}
                          </Box>
                        </>
                      );
                    }}
                  </FormDataConsumer>
                </SimpleFormIterator>
              </ArrayInput>
            );
          }
          return null;
        }}
      </FormDataConsumer>
      <NextSendNotice />

      {/* Conteudo da Mensagem */}
      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Mensagem</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
        <TextInput
          source="title"
          label="Título"
          validate={[required(), maxLength(65)]}
          InputProps={{ autoComplete: 'off', inputRef: titleInputRef }}
          sx={{ width: '50%' }}
        />
        <Box sx={{ mt: 2 }}>
          <CustomerNameTagButton inputRef={titleInputRef} />
        </Box>
      </Box>
      <Box sx={{
        width: 'auto',
        '& .ql-editor': {
          minHeight: 200
        }
      }}>
        <Labeled label="Descrição: ">
          <RichTextInputCustom
            source="body"
            label="Essa mensagem pode conter formatações e será exibida no aplicativo, na tela de notificação. Ela NÃO será enviada na notificação PUSH e no SMS"
            validate={required()}
          />
        </Labeled>
      </Box>
      <FormDataConsumer>
        {({ formData }) => {
          if (formData.sms) {
            return (
              <Labeled label="Esta mensagem será enviada no SMS: " sx={{ width: '70%' }}>
                <>
                  <TextInput
                    style={{ width: '100%' }}
                    source="smsMessage"
                    label="Mensagem SMS"
                    validate={[required(), maxLength(MAX_LENGTH_SMS)]}
                    inputProps={{ autoComplete: 'off' }}
                    onChange={(e) => counterCharInput('sms', e.target.value)}
                  />
                  <Box
                    component="span"
                    sx={{
                      fontSize: 12,
                      opacity: 0.75,
                      fontFamily: 'Helvetica',
                      textAlign: 'end',
                    }}
                  >
                    {counterCharSms}/{MAX_LENGTH_SMS}
                  </Box>
                </>
              </Labeled>
            );
          }
        }}
      </FormDataConsumer>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
        <Labeled label="Esta mensagem será enviada na notificação Push:" sx={{ width: '70%' }}>
          <>
            <TextInput
              style={{ width: '100%' }}
              source="pushMessage"
              label="Mensagem Push"
              validate={[required(), maxLength(MAX_LENGTH_PUSH)]}
              InputProps={{ autoComplete: 'off', inputRef: pushInputRef }}
              onChange={(e) => counterCharInput('push', e.target.value)}
              helperText={false}
            />
            <Box
              component="span"
              sx={{
                fontSize: 12,
                opacity: 0.75,
                fontFamily: 'Helvetica',
                textAlign: 'end',
              }}
            >
              {counterCharPush}/{MAX_LENGTH_PUSH}
            </Box>
          </>
        </Labeled>
        <Box sx={{ mt: 5 }}>
          <CustomerNameTagButton inputRef={pushInputRef} />
        </Box>
      </Box>
    </>
  );
};

export default FormRecurringSend;
